import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { CheckCircle2, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getOptimizedImageUrl } from "@/lib/cloudinary";
import { mediaService, storageService } from "@/services/firebase";
import toast from "react-hot-toast";
import type { MediaFile } from "@/types";
import { cn } from "@/lib/utils";

interface CloudinaryImageFieldProps {
  label: string;
  value?: string;
  folder: string;
  onChange: (value: string) => void;
  hint?: string;
}

export function CloudinaryImageField({
  label,
  value,
  folder,
  onChange,
  hint,
}: CloudinaryImageFieldProps) {
  const [uploading, setUploading] = useState(false);
  const [lastUploadedMedia, setLastUploadedMedia] = useState<MediaFile | null>(null);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [deletingMediaId, setDeletingMediaId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayFileName = lastUploadedMedia?.url === value
    ? lastUploadedMedia.name
    : value?.split("/").pop()?.split("?")[0] || "image liée";

  useEffect(() => {
    const unsubscribe = mediaService.subscribe((items) => {
      setMediaFiles(items.filter((item) => item.folder === folder));
    }, "createdAt");

    return () => unsubscribe();
  }, [folder]);

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const media = await storageService.uploadFile(file, folder);
      onChange(media.url);
      setLastUploadedMedia(media);
      toast.success("Image envoyée sur Cloudinary");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur lors de l'upload";
      toast.error(message);
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const handleSelect = (media: MediaFile) => {
    onChange(media.url);
    setLastUploadedMedia(media);
    toast.success("Image liée à cette section");
  };

  const handleDelete = async (media: MediaFile) => {
    setDeletingMediaId(media.id);
    try {
      await storageService.deleteFile(media.id, {
        publicId: media.publicId,
        resourceType: media.resourceType,
      });

      if (value === media.url) {
        onChange("");
      }

      toast.success("Image supprimée");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur lors de la suppression";
      toast.error(message);
    } finally {
      setDeletingMediaId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <label className="block text-sm font-medium text-dark">{label}</label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          isLoading={uploading}
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          Uploader
        </Button>
      </div>

      {value ? (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
          <img
            src={getOptimizedImageUrl(value, ["w_1200", "c_limit"])}
            alt={label}
            className="h-40 w-full object-cover"
          />
        </div>
      ) : (
        <div className="flex h-40 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-400">
          Aucun média sélectionné
        </div>
      )}

      <Input
        label="URL du média"
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder="https://res.cloudinary.com/..."
      />

      {value && (
        <div className="flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-medium">
              {lastUploadedMedia?.url === value ? "Image uploadée et liée à cette section" : "Image actuellement liée à cette section"}
            </p>
            <p className="text-xs text-green-600">{displayFileName} · dossier {folder}</p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium text-dark">Images déjà uploadées dans ce dossier</p>
          <span className="text-xs text-gray-500">{mediaFiles.length} fichier(s)</span>
        </div>

        {mediaFiles.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-500">
            Aucune image enregistrée dans `{folder}` pour le moment.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {mediaFiles.map((media) => {
              const isSelected = value === media.url;
              const isDeleting = deletingMediaId === media.id;

              return (
                <div
                  key={media.id}
                  className={cn(
                    "overflow-hidden rounded-xl border bg-white",
                    isSelected ? "border-primary ring-2 ring-primary/20" : "border-gray-200"
                  )}
                >
                  <img
                    src={getOptimizedImageUrl(media.url, ["w_400", "h_300", "c_fill"])}
                    alt={media.name}
                    className="h-28 w-full object-cover"
                  />
                  <div className="space-y-2 p-2">
                    <p className="truncate text-xs text-gray-600" title={media.name}>{media.name}</p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        className="flex-1"
                        variant={isSelected ? "secondary" : "outline"}
                        onClick={() => handleSelect(media)}
                      >
                        {isSelected ? "Sélectionnée" : "Utiliser"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(media)}
                        disabled={isDeleting}
                      >
                        {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {hint && <p className="text-xs text-gray-500">{hint}</p>}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleUpload}
        disabled={uploading}
      />
    </div>
  );
}
